import CircleButton from '@/components/CircleButton';
import CtaButton, { SecondaryCtaButton } from '@/components/CtaButton';
import CtaText from '@/components/CtaText';
import ErrorMessage from '@/components/ErrorMessage';
import FillImage from '@/components/FillImage';
import CheckboxInput from '@/components/Forms/CheckboxInput';
import TextInput from '@/components/Forms/TextInput';
import CheckMark from '@/components/Icons/CheckMark';
import LoadingSpinner from '@/components/Icons/LoadingSpinner';
import Pin from '@/components/Icons/Pin';
import PlusIcon from '@/components/Icons/PlusIcon';
import RightCheveron from '@/components/Icons/RightCheveron';
import UserAvatar from '@/components/Icons/UserAvatar';
import PageLayout from '@/components/PageLayout';
import Footer from '@/components/PageLayout/Footer';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import useHandleErrorLogic from '@/hooks/useHandleErrorLogic';
import { EventFormRegistrationSteps, EventLeadCapture } from '@/lib/interfaces';
import {
  CLIENT_PRIVACY_POLICY_URL,
  CLIENT_TERMS_AND_CONDITIONS_URL,
} from '@/utils/constants';
import { EventLeadCaptureSchema } from '@/utils/forms';
import { addEventLead } from '@/utils/v2HttpRequests';
import { Field, Form, Formik } from 'formik';
import { NextSeo } from 'next-seo';
import seo from 'next-seo.config';
import Link from 'next/link';
import { ReactNode, useState } from 'react';

export default function KaiWynwoodEventRegistrationPage() {
  return (
    <EventRegistration
      exhibition="Kai Art Miami 2022"
      events={[
        {
          name: 'ART MIAMI 2022',
          image:
            'https://res.cloudinary.com/hmkqf8kua/image/upload/v1669064405/ArtMiami2022/events/art_miami_mrjxht.png',
          description:
            "View Kai's latest solo show titled “Together” as part of his exhibition at Art Miami 2022",
          eventDate: 'Nov 29th - Dec 4th',
          eventTime: '11AM - 8PM',
          rsvpDetail:
            '“Together” can be viewed now at Art Miami, Markowicz Fine Art Booth AM 103',
          eventLocation:
            'One Herald Plaza (NE 14th Street & Biscayne Bay), Miami, FL 33132',
        },
        {
          name: 'KAI X WYNWOOD WALLS',
          image:
            'https://res.cloudinary.com/hmkqf8kua/image/upload/v1669064406/ArtMiami2022/events/wynwood_walls_y3bvnd.png',
          description:
            'Join Kai at Wynwood Walls for the 1 Year Anniversary of his monumental sculpture titled “Endless Possibilities”',
          eventDate: 'Dec 1st',
          eventTime: '4PM - 8PM',
          rsvpDetail:
            'Kai will be live screen printing during the event, alongside great music and fun. The first 100 visitors in line will receive a free screen printed sweatshirt by Kai',
          eventLocation: '2516 NW 2nd Ave, Miami, FL 33127',
        },
      ]}
      headerImageSrc={
        'https://res.cloudinary.com/hmkqf8kua/image/upload/v1668716207/ArtMiami2022/events/Together_FINAL_with_Background_Neon-01_w4w95p.jpg'
      }
      confirmation={
        <TwelveColGrid
          gap="gap-x-0.8 lgt:gap-x-2.4"
          my="mb-32 lgt:mb-48"
          addClassStyles="bg-gray-accent rounded-md"
        >
          <div className="col-span-12 lgt:col-span-8 lgt:col-start-3 h-49.6 flex flex-col items-center font-medium copy-xl">
            <span>CONFIRMED</span>
            <span className="font-normal text-black text-opacity-50 copy-l mt-2.4">
              REQUEST RECEIVED
            </span>
            <span className="font-normal text-black text-body mt-2.4">
              Details on how to secure attendance will be sent in the coming
              days.
            </span>
          </div>
        </TwelveColGrid>
      }
    />
  );
}

function EventRegistration({
  headerImageSrc,
  exhibition,
  events,
  confirmation,
}: Readonly<{
  headerImageSrc: string;
  exhibition: string;
  events: EventSelectionProps[];
  confirmation: ReactNode;
}>) {
  const [step, setStep] = useState(EventFormRegistrationSteps.REGISTER);
  const [hasGuest, setHasGuest] = useState(false);
  const [err, setErr] = useHandleErrorLogic();

  const initialValues: EventLeadCapture & {
    tcAgree: boolean;
    updatesAgree: boolean;
    eventSelection: string[];
  } = {
    firstName: '',
    lastName: '',
    guestFirstName: '',
    guestLastName: '',
    guestPhone: '',
    guestEmail: '',
    email: '',
    phone: '',
    tcAgree: false,
    updatesAgree: false,
    exhibition,
    event: '',
    eventSelection: ['', ''],
  };

  return (
    <PageLayout>
      <NextSeo
        {...seo}
        openGraph={{
          type: 'website',
          locale: 'en_IE',
          url: 'https://olea.art/',
          title: 'OLEA',
          description: 'OLEA',
          images: [
            {
              url: 'https://res.cloudinary.com/hmkqf8kua/image/upload/v1668716207/ArtMiami2022/events/Together_FINAL_with_Background_Neon-01_w4w95p.jpg',
              width: 32,
              height: 32,
              alt: 'OLEA',
              type: 'image/png',
            },
          ],
          site_name: 'OLEA',
        }}
      />
      <ErrorMessage error={err} />
      <NavBarLightTheme sticky={false} />

      <TwelveColGrid my="mb-6.4">
        <div className="col-span-12">
          <FillImage
            priority
            src={headerImageSrc}
            height="h-[55vw] max-h-[50rem]"
            objectFit="contain"
            width="w-full"
          />
        </div>
        {step === EventFormRegistrationSteps.REGISTER && (
          <div className="col-span-12 lgt:col-span-10 lgt:col-start-2 text-body lgt:text-mobile-xl text-black-300">
            Please indicate your interest across Kai’s Art Miami activations.
            Details on how to secure attendance will be sent following your
            interest submission below in the coming days.
          </div>
        )}
      </TwelveColGrid>

      {step === EventFormRegistrationSteps.REGISTER && (
        <TwelveColGrid>
          <Formik
            validationSchema={EventLeadCaptureSchema}
            initialValues={initialValues}
            onSubmit={async (values, actions) => {
              if (!values?.guestEmail) {
                values.guestEmail = null;
              }
              try {
                const noEventSelected = values.eventSelection.every(
                  (v) => v.length === 0
                );
                if (noEventSelected) {
                  actions.setFieldError(
                    'event',
                    'Please Select At least 1 event.'
                  );
                  return;
                }

                await addEventLead({
                  ...values,
                  event: values.eventSelection.join(' '),
                });
                setStep(EventFormRegistrationSteps.CONFIRM);
                actions.setSubmitting(false);
              } catch (error) {
                actions.setSubmitting(false);
                console.log(error);
                setErr(error);
              }
            }}
          >
            {({ isSubmitting, values, setFieldValue, errors }) => (
              <>
                <h3 className="my-1.6 copy-l col-span-12 lgt:col-span-11 lgt:col-start-2 font-medium">
                  CHOOSE EVENT(S)
                </h3>
                <div className="opacity-0 lgt:col-span-1">here</div>
                <EventSelection {...events[0]}>
                  <CtaButton
                    height="h-4.8"
                    margin="mx-auto"
                    onClick={() =>
                      setFieldValue('eventSelection', [
                        values.eventSelection[0].length ? '' : events[0].name,
                        values.eventSelection[1],
                      ])
                    }
                    background={'bg-white'}
                    border={'border border-black'}
                    color={'text-black'}
                  >
                    {values.eventSelection[0].length ? (
                      <>
                        <CtaText>SELECTED</CtaText>
                        <CheckMark hover="" />
                      </>
                    ) : (
                      <CtaText>CLICK TO SELECT</CtaText>
                    )}
                  </CtaButton>
                  {errors.eventSelection && (
                    <div className="w-full max-w-xl mx-auto text-xs text-red-error mt-0.4">
                      {errors.eventSelection}
                    </div>
                  )}
                </EventSelection>
                <EventSelection {...events[1]}>
                  <CtaButton
                    height="h-4.8"
                    margin="mx-auto"
                    onClick={() =>
                      setFieldValue('eventSelection', [
                        values.eventSelection[0],
                        values.eventSelection[1].length ? '' : events[1].name,
                      ])
                    }
                    background={'bg-white'}
                    border={'border border-black'}
                    color={'text-black'}
                  >
                    {values.eventSelection[1].length ? (
                      <>
                        <CtaText>SELECTED</CtaText>
                        <CheckMark hover="" />
                      </>
                    ) : (
                      <CtaText>CLICK TO SELECT</CtaText>
                    )}
                  </CtaButton>
                  {errors.eventSelection && (
                    <div className="w-full max-w-xl mx-auto text-xs text-red-error mt-0.4">
                      {errors.eventSelection}
                    </div>
                  )}
                </EventSelection>

                <Form className="flex flex-col col-span-12 lgt:col-span-10 lgt:col-start-2">
                  <h3 className="my-1.6 copy-l">PERSONAL INFORMATION</h3>
                  <Field
                    type="text"
                    name="firstName"
                    placeholder=" "
                    labelCopy="First name"
                    component={TextInput}
                  />
                  <Field
                    type="text"
                    name="lastName"
                    placeholder=" "
                    labelCopy="Last name"
                    component={TextInput}
                  />
                  <Field
                    type="email"
                    name="email"
                    placeholder=" "
                    labelCopy="Email"
                    component={TextInput}
                  />
                  <Field
                    type="tel"
                    name="phone"
                    placeholder=" "
                    labelCopy="Phone"
                    component={TextInput}
                  />
                  {hasGuest && (
                    <>
                      <h3 className="my-1.6 copy-l">GUEST INFORMATION</h3>
                      <Field
                        type="text"
                        name="guestFirstName"
                        placeholder=" "
                        labelCopy="First name"
                        component={TextInput}
                      />
                      <Field
                        type="text"
                        name="guestLastName"
                        placeholder=" "
                        labelCopy="Last name"
                        component={TextInput}
                      />
                      <Field
                        type="email"
                        name="guestEmail"
                        placeholder=" "
                        labelCopy="Email"
                        component={TextInput}
                      />
                      <Field
                        type="tel"
                        name="guestPhone"
                        placeholder=" "
                        labelCopy="Phone"
                        component={TextInput}
                      />
                    </>
                  )}
                  <div className="mb-1.6">
                    <Field
                      checkboxCopy="I want to recieve updates and communication from OLEA"
                      type="checkbox"
                      name="updatesAgree"
                      component={CheckboxInput}
                    />
                  </div>
                  <div className="mb-1.6">
                    <Field
                      checkboxCopy={
                        <>
                          I agree to the{' '}
                          <Link
                            href={CLIENT_PRIVACY_POLICY_URL}
                            className="text-black mx-0.3 font-medium"
                          >
                            Privacy Policy
                          </Link>
                          and
                          <Link
                            href={CLIENT_TERMS_AND_CONDITIONS_URL}
                            className="text-black mx-0.3 font-medium"
                          >
                            Terms &amp; Conditions
                          </Link>
                        </>
                      }
                      type="checkbox"
                      name="tcAgree"
                      component={CheckboxInput}
                    />
                  </div>

                  <div className="flex my-2.4 justify-between">
                    {!hasGuest && (
                      <SecondaryCtaButton
                        height="h-4.8"
                        onClick={() => setHasGuest(true)}
                      >
                        <CtaText>INVITE GUEST</CtaText>
                        <PlusIcon />
                      </SecondaryCtaButton>
                    )}
                    <CtaButton
                      disabled={isSubmitting}
                      type="submit"
                      height="h-4.8"
                    >
                      {isSubmitting ? (
                        <>
                          <CtaText>PROCESSING</CtaText>
                          <LoadingSpinner />
                        </>
                      ) : (
                        <>
                          <CtaText>RSVP</CtaText>
                          <RightCheveron />
                        </>
                      )}
                    </CtaButton>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </TwelveColGrid>
      )}
      {step === EventFormRegistrationSteps.CONFIRM && confirmation}
      <Footer />
    </PageLayout>
  );
}

interface EventSelectionProps {
  image: string;
  name: string;
  description: string;
  eventDate: string;
  eventTime: string;
  rsvpDetail: string;
  eventLocation: string;
}

function EventSelection({
  image,
  name,
  description,
  eventDate,
  eventTime,
  rsvpDetail,
  eventLocation,
  children,
}: EventSelectionProps & { children: ReactNode }) {
  return (
    <div className="col-span-12 lgt:col-span-5 mt-1.6 lgt:mt-0">
      <div className="bg-grey-100 p-2.4">
        <div className="flex flex-col items-center">
          <FillImage src={image} height="h-40" width="w-40" />
          <h5 className="ml-1.6 copy-l">{name}</h5>
        </div>
        <p className="my-5.6 text-body text-black-300">{description}</p>
        <div className="mb-1.6 flex justify-between items-center">
          <div className="flex items-center">
            <CircleButton>
              <PlusIcon />
            </CircleButton>
            <p className="ml-1.6 text-body">{eventDate}</p>
          </div>
          <p className="ml-1.6 text-body">{eventTime}</p>
        </div>
        <div className="mb-1.6 flex justify-between">
          <div className="flex items-center">
            <CircleButton>
              <UserAvatar />
            </CircleButton>
            <p className="ml-1.6 text-body min-h-[10rem]">{rsvpDetail}</p>
          </div>
        </div>
        <div className="mb-1.6 flex justify-between h-4.8">
          <div className="flex">
            <CircleButton>
              <Pin />
            </CircleButton>
            <p className="ml-1.6 text-body">{eventLocation}</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
